import { useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { useConfiguredBrandsQuery } from '../../queries/brandMetrics';
import {
  useCalendarCreatePromoMutation,
  useCalendarDeletePromoMutation,
  usePromoCalendarQuery,
} from '../../queries/mediaPlanning';
import { DayHeader } from './tableChrome';
import PromoTimelineRow from './PromoTimelineRow';

const currentMonth = () => new Date().toISOString().slice(0, 7);
const todayIso = () => new Date().toISOString().slice(0, 10);
const controlCls = 'rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-700';

function Notice({ children }) {
  return (
    <div className="rounded-card border border-gray-200 bg-white p-10 text-center text-sm text-gray-500 shadow-card">
      {children}
    </div>
  );
}

export default function PromoCalendarPage() {
  const [month, setMonth] = useState(currentMonth);
  const [adding, setAdding] = useState(false);
  const { data: brands = [] } = useConfiguredBrandsQuery();
  const { data, isLoading, isError, refetch } = usePromoCalendarQuery(month);
  const createPromo = useCalendarCreatePromoMutation(month);
  const deletePromo = useCalendarDeletePromoMutation(month);

  const today = todayIso();
  const days = data?.days ?? [];
  const globalPromos = data?.global_promos ?? [];
  const brandRows = data?.brands ?? [];
  const isEmpty = !isLoading && !isError && globalPromos.length === 0 && brandRows.length === 0;

  const emptyForm = { brandId: '', name: '', from_date: `${month}-01`, to_date: `${month}-01` };
  const [form, setForm] = useState(emptyForm);
  const openForm = () => {
    setForm({ ...emptyForm, from_date: `${month}-01`, to_date: `${month}-01` });
    setAdding(true);
  };

  const submit = async () => {
    const name = form.name.trim();
    if (!form.brandId || !name || !form.from_date || !form.to_date) {
      toast.error('Completá marca, nombre y fechas');
      return;
    }
    if (form.to_date < form.from_date) {
      toast.error('La fecha de fin debe ser posterior o igual al inicio');
      return;
    }
    try {
      await createPromo.mutateAsync({
        brandId: Number(form.brandId),
        name,
        from_date: form.from_date,
        to_date: form.to_date,
      });
      toast.success('Promo agregada');
      setAdding(false);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo agregar la promo');
    }
  };

  const remove = async (brandId, promo) => {
    try {
      await deletePromo.mutateAsync({ brandId, promoId: promo.id });
    } catch (_e) {
      toast.error('No se pudo quitar la promo');
    }
  };

  return (
    <div className="mx-auto max-w-[110rem] space-y-6 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Calendario de promos</h1>
          <p className="text-sm text-gray-500">
            Las promos de todas las cuentas juntas + el calendario comercial. Marcadores informativos:
            no cambian presupuestos.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="month"
            aria-label="Mes"
            className={controlCls}
            value={month}
            onChange={(e) => setMonth(e.target.value || currentMonth())}
          />
          <button
            type="button"
            onClick={openForm}
            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
          >
            <Plus size={15} /> Agregar promo
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-4 rounded-full bg-sky-200" /> Calendario comercial</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-4 rounded-full bg-fuchsia-200" /> Promos de la marca</span>
      </div>

      {isLoading ? (
        <Notice>Cargando calendario…</Notice>
      ) : isError ? (
        <Notice>
          No se pudo cargar el calendario.{' '}
          <button type="button" onClick={() => refetch()} className="font-medium text-primary hover:underline">
            Reintentar
          </button>
        </Notice>
      ) : isEmpty ? (
        <Notice>No hay promos cargadas para este mes. Agregá una con “Agregar promo”.</Notice>
      ) : (
        <div className="overflow-x-auto rounded-card border border-gray-200 bg-white shadow-card">
          <table className="w-full border-separate border-spacing-0 text-right text-sm">
            <DayHeader days={days} firstLabel="Cuenta" today={today} />
            <tbody>
              {globalPromos.length > 0 && (
                <PromoTimelineRow
                  label="Calendario comercial"
                  promos={globalPromos}
                  days={days}
                  today={today}
                  tone="sky"
                />
              )}
              {brandRows.map((b) => (
                <PromoTimelineRow
                  key={b.brand_id}
                  label={b.name || `#${b.brand_id}`}
                  promos={b.promos}
                  days={days}
                  today={today}
                  tone="fuchsia"
                  onDelete={(promo) => remove(b.brand_id, promo)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAdding(false)}>
            <div className="w-full max-w-sm rounded-card bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-base font-semibold text-gray-900">Agregar promo</h3>
              <p className="mt-1 text-sm text-gray-500">Marca las fechas en que se viene una promo. No cambia presupuestos.</p>
              <div className="mt-4 space-y-3">
                <label className="block text-sm">
                  <span className="text-gray-600">Cuenta</span>
                  <select
                    autoFocus
                    value={form.brandId}
                    onChange={(e) => setForm({ ...form, brandId: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm"
                  >
                    <option value="">Elegí una marca…</option>
                    {brands.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">Nombre</span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ej: Sale de invierno"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm"
                  />
                </label>
                <div className="flex gap-3">
                  <label className="block flex-1 text-sm">
                    <span className="text-gray-600">Desde</span>
                    <input
                      type="date"
                      value={form.from_date}
                      onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm"
                    />
                  </label>
                  <label className="block flex-1 text-sm">
                    <span className="text-gray-600">Hasta</span>
                    <input
                      type="date"
                      value={form.to_date}
                      onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm"
                    />
                  </label>
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button type="button" onClick={() => setAdding(false)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={createPromo.isPending}
                  className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  {createPromo.isPending ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
